import type { Admin, ArticleDetail, ArticleListItem, ArticlePayload, Category, ContentType, SiteId, MediaAsset, PublicationJob } from "./types";
import type { Homepage, Placement, ButtonStyle, Colors, Focus } from './homepage-types';

export class ApiError extends Error {
  code: string;
  details?: unknown;
  status: number;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let csrfToken = "";
export function setCsrfToken(value: string) { csrfToken = value; }

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  if (init.method && !["GET", "HEAD"].includes(init.method.toUpperCase()) && csrfToken) headers.set("X-CSRF-Token", csrfToken);
  const response = await fetch(path, { ...init, headers, credentials: "include" });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: { code: "unknown", message: "Не удалось выполнить запрос" } }));
    throw new ApiError(response.status, payload.error?.code ?? "unknown", payload.error?.message ?? "Не удалось выполнить запрос", payload.error?.details);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  homepage: () => request<Homepage>('/api/homepage'),
  saveHomepage: (payload: { version: number; slides: unknown[] }) => request<Homepage>('/api/homepage', { method: 'PUT', body: JSON.stringify(payload) }),
  publishHomepage: (version: number, revisionId: string) => request<PublicationJob>('/api/homepage/publish', { method: 'POST', body: JSON.stringify({ version, revisionId }) }),
  homepageStyle: (mediaId: string, placement: Placement, colors?: Colors, focus?: Focus, ratio = '4:1') => request<ButtonStyle>('/api/homepage/cta-style', { method: 'POST', body: JSON.stringify({ mediaId, placement, colors, focus, ratio }) }),
  uploadHomepageCover: (file: File, profile: 'desktop' | 'mobile') => {
    const body = new FormData(); body.append('file', file); body.append('profile', profile);
    return request<MediaAsset>('/api/homepage/cover', { method: 'POST', body });
  },
  async login(username: string, password: string) {
    const admin = await request<Admin>("/api/auth/login", { method: "POST", body: JSON.stringify({ username, password }) });
    setCsrfToken(admin.csrfToken);
    return admin;
  },
  async me() {
    const admin = await request<Admin>("/api/auth/me");
    setCsrfToken(admin.csrfToken);
    return admin;
  },
  async logout() {
    await request<void>("/api/auth/logout", { method: "POST" });
    setCsrfToken("");
  },
  categories: () => request<Category[]>("/api/categories"),
  articles: (search = "", status = "", contentType: ContentType = "ARTICLE", siteId: SiteId | "" = "") => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (status) params.set("status", status);
    params.set("contentType", contentType);
    if (siteId) params.set("siteId", siteId);
    return request<{ items: ArticleListItem[]; total: number }>(`/api/articles?${params}`);
  },
  article: (id: string) => request<ArticleDetail>(`/api/articles/${id}`),
  createArticle: (payload: ArticlePayload) => request<ArticleDetail>("/api/articles", { method: "POST", body: JSON.stringify(payload) }),
  updateArticle: (id: string, payload: ArticlePayload & { version: number }) => request<ArticleDetail>(`/api/articles/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  confirmLegalReview: (id: string, version: number, revisionId: string) => request<ArticleDetail>(`/api/articles/${id}/legal-review`, { method: "POST", body: JSON.stringify({ version, revisionId }) }),
  deleteArticle: (id: string, version: number) => request<void>(`/api/articles/${id}`, { method: "DELETE", headers: { "If-Match": String(version) } }),
  media: (articleId?: string) => request<MediaAsset[]>(`/api/media${articleId ? `?articleId=${encodeURIComponent(articleId)}` : ""}`),
  uploadMedia: (file: File, articleId?: string) => {
    const body = new FormData();
    body.append("file", file);
    if (articleId) body.append("articleId", articleId);
    return request<MediaAsset>("/api/media", { method: "POST", body });
  },
  deleteMedia: (id: string) => request<void>(`/api/media/${id}`, { method: "DELETE" }),
  publishArticle: (id: string) => request<PublicationJob>(`/api/articles/${id}/publish`, { method: "POST" }),
  latestPublication: (id: string) => request<PublicationJob | null>(`/api/articles/${id}/publications/latest`),
  publication: (id: string) => request<PublicationJob>(`/api/publications/${id}`),
};

